/**
 * FinanzAufgabenPage — Aufgaben im Finanzen-Cockpit (Sektion „Aufgaben",
 * Route /finanzen/:firma/aufgaben)
 *
 * Eingebettet im Mandanten-Cockpit. Zeigt die Tabelle `finanz_aufgaben` als
 * zentralen To-do-Bereich der Buchhaltung — ruhig (slate/zinc), ein einziger
 * emerald-Akzent. Aufgaben lassen sich filtern, im Status weiterklicken und
 * neu anlegen.
 *
 * Prop `firmaKey`:
 *   'sv'   -> SV Buero  (finanz_aufgaben.firma = 'sv_buero_koop')
 *   'lcs'  -> LCS       (finanz_aufgaben.firma = 'lcs')
 *   'alle' -> alle Firmen
 * Aufgaben mit firma IS NULL werden in jeder Ansicht mit angezeigt
 * (firmenuebergreifende To-dos).
 *
 * TABELLE finanz_aufgaben (nur lesen + Status-UPDATE + INSERT, keine Migration):
 *   id (uuid), titel, beschreibung, firma (nullable), kategorie, status,
 *   prioritaet, faellig (date), quelle, created_at
 *
 * RLS/Policies bleiben unangetastet (Inhaber-only).
 * Alle Abfragen sind robust gegen fehlende Tabellen/leere Daten (kein Absturz).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  ListChecks, Loader2, Inbox, Plus, X, Check, ChevronRight, Clock, Hourglass,
} from 'lucide-react';

// ============================================================
// Firmen-Mapping
// ============================================================

export type AufgabenFirmaKey = 'sv' | 'lcs' | 'alle';

/** firmaKey -> konkreter finanz_aufgaben.firma-String (fuer Filter/Insert). */
const FIRMA_STRING: Record<Exclude<AufgabenFirmaKey, 'alle'>, string> = {
  sv: 'sv_buero_koop',
  lcs: 'lcs',
};

const FIRMA_LABEL: Record<string, string> = {
  sv_buero_koop: 'SV Büro',
  lcs: 'LCS',
  prolea_fzco: 'Prolea FZCO',
  pv_gbr: 'PV GbR',
};

// ============================================================
// Status / Kategorie / Prioritaet — Stammwerte
// ============================================================

type Status = 'offen' | 'in_arbeit' | 'wartet_extern' | 'erledigt';
type Kategorie = 'struktur' | 'ust' | 'belege' | 'buchung' | 'klaerung' | 'stb' | 'sicherheit';
type Prioritaet = 'hoch' | 'mittel' | 'niedrig';

const STATUS_LABEL: Record<Status, string> = {
  offen: 'Offen',
  in_arbeit: 'In Arbeit',
  wartet_extern: 'Wartet extern',
  erledigt: 'Erledigt',
};

/** Reihenfolge der Status-Filter-Chips (inkl. "Alle"). */
const STATUS_REIHENFOLGE: Status[] = ['offen', 'in_arbeit', 'wartet_extern', 'erledigt'];

const KATEGORIE_LABEL: Record<Kategorie, string> = {
  struktur: 'Struktur',
  ust: 'USt',
  belege: 'Belege',
  buchung: 'Buchung',
  klaerung: 'Klärung',
  stb: 'StB',
  sicherheit: 'Sicherheit',
};

const KATEGORIE_REIHENFOLGE: Kategorie[] = [
  'struktur', 'ust', 'belege', 'buchung', 'klaerung', 'stb', 'sicherheit',
];

const PRIORITAET_LABEL: Record<Prioritaet, string> = {
  hoch: 'Hoch',
  mittel: 'Mittel',
  niedrig: 'Niedrig',
};

/** Naechster Status im Arbeitsfluss (offen -> in_arbeit -> wartet_extern -> erledigt). */
const NAECHSTER_STATUS: Record<Status, Status | null> = {
  offen: 'in_arbeit',
  in_arbeit: 'wartet_extern',
  wartet_extern: 'erledigt',
  erledigt: null,
};

// ============================================================
// Typen
// ============================================================

interface AufgabeRow {
  id: string;
  titel: string | null;
  beschreibung: string | null;
  firma: string | null;
  kategorie: string | null;
  status: string | null;
  prioritaet: string | null;
  faellig: string | null;
  quelle: string | null;
  created_at: string | null;
}

// ============================================================
// Helpers
// ============================================================

function isTableNotFound(error: unknown): boolean {
  if (!error) return false;
  const e = error as { message?: string; code?: string };
  const msg = (e.message || '') + ' ' + (e.code || '');
  return (
    msg.includes('PGRST205') ||
    msg.includes('schema cache') ||
    msg.includes('relation') ||
    msg.includes('does not exist')
  );
}

function normStatus(v: string | null | undefined): Status {
  return v === 'in_arbeit' || v === 'wartet_extern' || v === 'erledigt' ? v : 'offen';
}

function normPrio(v: string | null | undefined): Prioritaet {
  return v === 'hoch' || v === 'mittel' || v === 'niedrig' ? v : 'mittel';
}

/** Sortier-Reihenfolge: offen/in_arbeit zuerst, dann Prioritaet hoch, dann created_at. */
const STATUS_SORT: Record<Status, number> = {
  offen: 0,
  in_arbeit: 1,
  wartet_extern: 2,
  erledigt: 3,
};

const PRIO_SORT: Record<Prioritaet, number> = {
  hoch: 0,
  mittel: 1,
  niedrig: 2,
};

function fmtDatum(d: string | null): string {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ============================================================
// Daten-Layer
// ============================================================

/** Laedt alle relevanten Aufgaben (firmengefiltert, NULL-Firma immer dabei). */
async function ladeAufgaben(firmaKey: AufgabenFirmaKey): Promise<AufgabeRow[]> {
  try {
    let query = supabase
      .from('finanz_aufgaben')
      .select('id, titel, beschreibung, firma, kategorie, status, prioritaet, faellig, quelle, created_at')
      .limit(5000);

    if (firmaKey !== 'alle') {
      const firmaStr = FIRMA_STRING[firmaKey];
      // Aufgaben der Firma ODER ohne Firma (firmenuebergreifend).
      query = query.or(`firma.eq.${firmaStr},firma.is.null`);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data as AufgabeRow[]) || [];
  } catch (err) {
    if (!isTableNotFound(err)) console.error('Aufgaben laden fehlgeschlagen:', err);
    return [];
  }
}

// ============================================================
// UI-Bausteine
// ============================================================

/** Dezentes Badge (slate). */
function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-600 border border-slate-200">
      {children}
    </span>
  );
}

/** Prioritaets-Badge — dezent, "hoch" leicht betont. */
function PrioBadge({ prio }: { prio: Prioritaet }) {
  const cls =
    prio === 'hoch'
      ? 'bg-slate-800 text-white border-slate-800'
      : 'bg-slate-100 text-slate-500 border-slate-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ${cls}`}>
      {PRIORITAET_LABEL[prio]}
    </span>
  );
}

/** Filter-Chip. */
function Chip({
  aktiv, onClick, children,
}: {
  aktiv: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
        aktiv
          ? 'bg-emerald-600 text-white border-emerald-600'
          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}

/** Eine Aufgaben-Zeile. */
function AufgabeItem({
  a, onStatus, busy,
}: {
  a: AufgabeRow;
  onStatus: (id: string, status: Status) => void;
  busy: boolean;
}) {
  const status = normStatus(a.status);
  const prio = normPrio(a.prioritaet);
  const erledigt = status === 'erledigt';
  const naechster = NAECHSTER_STATUS[status];
  const kategorie = (a.kategorie as Kategorie) || null;

  return (
    <div
      className={`px-4 py-3.5 border-t border-slate-100 first:border-t-0 ${
        erledigt ? 'bg-slate-50/40' : 'bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${erledigt ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
            {a.titel || 'Ohne Titel'}
          </div>
          {a.beschreibung && (
            <p className={`text-xs mt-0.5 leading-relaxed ${erledigt ? 'text-slate-300' : 'text-slate-500'}`}>
              {a.beschreibung}
            </p>
          )}
          <div className="flex items-center gap-1.5 flex-wrap mt-2">
            {kategorie && KATEGORIE_LABEL[kategorie] && <Badge>{KATEGORIE_LABEL[kategorie]}</Badge>}
            {a.firma && FIRMA_LABEL[a.firma] && <Badge>{FIRMA_LABEL[a.firma]}</Badge>}
            <PrioBadge prio={prio} />
            {a.faellig && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                <Clock className="w-3 h-3" />
                fällig {fmtDatum(a.faellig)}
              </span>
            )}
          </div>
        </div>

        {/* Status-Steuerung */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
            {status === 'wartet_extern' && <Hourglass className="w-3 h-3" />}
            {STATUS_LABEL[status]}
          </span>
          {!erledigt && naechster && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onStatus(a.id, naechster)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              title={`Auf "${STATUS_LABEL[naechster]}" setzen`}
            >
              {STATUS_LABEL[naechster]}
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
          {!erledigt && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onStatus(a.id, 'erledigt')}
              className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-50"
              title="Als erledigt markieren"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
          {erledigt && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onStatus(a.id, 'offen')}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-400 border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
              title="Wieder öffnen"
            >
              Wieder öffnen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Neue-Aufgabe-Formular (inline)
// ============================================================

interface NeueAufgabe {
  titel: string;
  beschreibung: string;
  kategorie: Kategorie;
  firma: string; // '' = firmenuebergreifend (NULL)
  prioritaet: Prioritaet;
}

function leereAufgabe(firmaKey: AufgabenFirmaKey): NeueAufgabe {
  return {
    titel: '',
    beschreibung: '',
    kategorie: 'buchung',
    firma: firmaKey !== 'alle' ? FIRMA_STRING[firmaKey] : '',
    prioritaet: 'mittel',
  };
}

function NeueAufgabeForm({
  firmaKey, onAbbruch, onGespeichert,
}: {
  firmaKey: AufgabenFirmaKey;
  onAbbruch: () => void;
  onGespeichert: () => void;
}) {
  const [form, setForm] = useState<NeueAufgabe>(() => leereAufgabe(firmaKey));
  const [saving, setSaving] = useState(false);

  const speichern = async () => {
    if (!form.titel.trim()) {
      toast.error('Bitte einen Titel angeben.');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.from('finanz_aufgaben').insert({
        titel: form.titel.trim(),
        beschreibung: form.beschreibung.trim() || null,
        kategorie: form.kategorie,
        firma: form.firma || null,
        prioritaet: form.prioritaet,
        status: 'offen',
        quelle: 'manuell',
      });
      if (error) throw error;
      toast.success('Aufgabe angelegt.');
      onGespeichert();
    } catch (err) {
      console.error('Aufgabe anlegen fehlgeschlagen:', err);
      toast.error('Aufgabe konnte nicht angelegt werden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">Neue Aufgabe</h3>
        <button
          type="button"
          onClick={onAbbruch}
          className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-50"
          title="Abbrechen"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Titel</label>
        <input
          type="text"
          value={form.titel}
          onChange={(e) => setForm((f) => ({ ...f, titel: e.target.value }))}
          placeholder="Was ist zu tun?"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Beschreibung</label>
        <textarea
          value={form.beschreibung}
          onChange={(e) => setForm((f) => ({ ...f, beschreibung: e.target.value }))}
          placeholder="Optionale Details"
          rows={2}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400 resize-none"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Kategorie</label>
          <select
            value={form.kategorie}
            onChange={(e) => setForm((f) => ({ ...f, kategorie: e.target.value as Kategorie }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          >
            {KATEGORIE_REIHENFOLGE.map((k) => (
              <option key={k} value={k}>{KATEGORIE_LABEL[k]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Firma</label>
          <select
            value={form.firma}
            onChange={(e) => setForm((f) => ({ ...f, firma: e.target.value }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          >
            <option value="">Firmenübergreifend</option>
            <option value="sv_buero_koop">SV Büro</option>
            <option value="lcs">LCS</option>
            <option value="prolea_fzco">Prolea FZCO</option>
            <option value="pv_gbr">PV GbR</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Priorität</label>
          <select
            value={form.prioritaet}
            onChange={(e) => setForm((f) => ({ ...f, prioritaet: e.target.value as Prioritaet }))}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          >
            <option value="hoch">Hoch</option>
            <option value="mittel">Mittel</option>
            <option value="niedrig">Niedrig</option>
          </select>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onAbbruch}
          className="px-3 py-2 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={speichern}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Aufgabe anlegen
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Hauptkomponente
// ============================================================

export default function FinanzAufgabenPage({ firmaKey = 'sv' }: { firmaKey?: AufgabenFirmaKey }) {
  const [loading, setLoading] = useState(true);
  const [aufgaben, setAufgaben] = useState<AufgabeRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<Status | 'alle'>('alle');
  const [kategorieFilter, setKategorieFilter] = useState<Kategorie | 'alle'>('alle');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);

  const lade = useCallback(async () => {
    setLoading(true);
    const data = await ladeAufgaben(firmaKey);
    setAufgaben(data);
    setLoading(false);
  }, [firmaKey]);

  useEffect(() => {
    lade();
  }, [lade]);

  // ── Status-Zaehler (ueber alle geladenen Aufgaben) ──
  const zaehler = useMemo(() => {
    const z: Record<Status, number> = { offen: 0, in_arbeit: 0, wartet_extern: 0, erledigt: 0 };
    for (const a of aufgaben) z[normStatus(a.status)] += 1;
    return z;
  }, [aufgaben]);

  // ── Sortierung + Filter ──
  const sichtbar = useMemo(() => {
    const gefiltert = aufgaben.filter((a) => {
      if (statusFilter !== 'alle' && normStatus(a.status) !== statusFilter) return false;
      if (kategorieFilter !== 'alle' && a.kategorie !== kategorieFilter) return false;
      return true;
    });
    return gefiltert.slice().sort((x, y) => {
      const sx = STATUS_SORT[normStatus(x.status)];
      const sy = STATUS_SORT[normStatus(y.status)];
      if (sx !== sy) return sx - sy;
      const px = PRIO_SORT[normPrio(x.prioritaet)];
      const py = PRIO_SORT[normPrio(y.prioritaet)];
      if (px !== py) return px - py;
      const cx = x.created_at || '';
      const cy = y.created_at || '';
      return cx < cy ? -1 : cx > cy ? 1 : 0;
    });
  }, [aufgaben, statusFilter, kategorieFilter]);

  // ── Status aendern (optimistisch, mit Rollback bei Fehler) ──
  const aendereStatus = async (id: string, status: Status) => {
    setBusyId(id);
    const vorher = aufgaben;
    setAufgaben((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      const { error } = await supabase
        .from('finanz_aufgaben')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      toast.success(`Status: ${STATUS_LABEL[status]}`);
    } catch (err) {
      console.error('Status aendern fehlgeschlagen:', err);
      toast.error('Status konnte nicht geändert werden.');
      setAufgaben(vorher);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Kopf */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
            <ListChecks className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Aufgaben</h2>
            <p className="text-xs text-slate-400">Zentraler To-do-Bereich der Buchhaltung</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setFormOffen((o) => !o)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4" />
          Neue Aufgabe
        </button>
      </div>

      {/* Status-Zaehler */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {STATUS_REIHENFOLGE.map((s) => (
          <div key={s} className="bg-white border border-slate-200 rounded-xl p-3.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {STATUS_LABEL[s]}
            </div>
            <div className="text-2xl font-bold text-slate-900 mt-0.5 tabular-nums">{zaehler[s]}</div>
          </div>
        ))}
      </div>

      {/* Neue-Aufgabe-Formular */}
      {formOffen && (
        <NeueAufgabeForm
          firmaKey={firmaKey}
          onAbbruch={() => setFormOffen(false)}
          onGespeichert={() => {
            setFormOffen(false);
            lade();
          }}
        />
      )}

      {/* Filter */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mr-1">Status</span>
          <Chip aktiv={statusFilter === 'alle'} onClick={() => setStatusFilter('alle')}>Alle</Chip>
          {STATUS_REIHENFOLGE.map((s) => (
            <Chip key={s} aktiv={statusFilter === s} onClick={() => setStatusFilter(s)}>
              {STATUS_LABEL[s]}
            </Chip>
          ))}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mr-1">Kategorie</span>
          <Chip aktiv={kategorieFilter === 'alle'} onClick={() => setKategorieFilter('alle')}>Alle</Chip>
          {KATEGORIE_REIHENFOLGE.map((k) => (
            <Chip key={k} aktiv={kategorieFilter === k} onClick={() => setKategorieFilter(k)}>
              {KATEGORIE_LABEL[k]}
            </Chip>
          ))}
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : sichtbar.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
          <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-6 h-6 text-slate-300" />
          </div>
          <h3 className="text-base font-bold text-slate-900">
            {aufgaben.length === 0 ? 'Noch keine Aufgaben' : 'Keine Aufgaben für diesen Filter'}
          </h3>
          <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto leading-relaxed">
            {aufgaben.length === 0
              ? 'Lege oben eine neue Aufgabe an, um den To-do-Bereich der Buchhaltung zu füllen.'
              : 'Passe die Filter an oder lege eine neue Aufgabe an.'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {sichtbar.map((a) => (
            <AufgabeItem key={a.id} a={a} onStatus={aendereStatus} busy={busyId === a.id} />
          ))}
        </div>
      )}
    </div>
  );
}
